import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layout from "../../Components/Layout";
import Stepper from "../../Components/Admin/Stepper";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import { getActiveBrandConfig, getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";
import { isTeachrAccountActive } from "../../Utils/Function";
import { DeclarationContexteType, DeclarationProvider } from "../../Contexts/DeclarationContexte";
import { DispatchClientParams } from "../../config/CLIENT_TYPE_CONFIG";
import { store } from "../../Redux/store";
import { getActiveClients } from "../../Redux/Actions/StudentActions";
import IndexApp from "../../Components/IndexApp";
import FirstStep from "../../App/DeclarationCourse/Steps/FirstStep";
import SecondStep from "../../App/DeclarationCourse/Steps/SecondStep";
import ThirdStep from "../../App/DeclarationCourse/Steps/ThirdStep";
import LastStep from "../../App/DeclarationCourse/Steps/LastStep";
import FinishStep from "../../App/DeclarationCourse/Steps/FinishStep";

const DeclarationPage = () => {
    const { classes, common } = useTailwindBrand();
    const teachr = useSelector((state: any) => state.user.currentUser.teachr);
    const activeClientsState = useSelector((state: any) => state?.student?.activeClients);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const generalStep = useSelector((state: any) => state.declarationCourse);
    const step = generalStep?.step;
    const [currentStep, setCurrentStep] = useState<any>(step ?? 1);
    const handleChangeStep = (val: number) => setCurrentStep(val);
    // ── Pagination & recherche ────────────────────────────────────────────
    const [nameSearch, setNameSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(0);
    const [limit, setLimit] = useState(10);

    const paginate = useCallback((page: number) => setCurrentPage(page), []);
    const pageLimit = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(0);
    }, []);

    const dataObject: DispatchClientParams = useMemo(() => ({
        teachrId: teachr?.id,
        archive: 0,
        page: currentPage,
        variable: 0,
        sort: 1,
        limit,
        validated: 1,
        search: nameSearch,
        onlyActive: true,
    }), [teachr?.id, currentPage, limit, nameSearch]);

    // Garde : un prof dont le compte n'est pas actif ne déclare pas, même en tapant l'URL.
    // Le backend refuse aussi la création du cours (TeachrDeclarationGuard).
    useEffect(() => {
        if (teachr?.id && !isTeachrAccountActive(teachr)) {
            navigate(`/${getActiveBrandConfig()}/profile/complete`, { replace: true });
        }
    }, [teachr, navigate]);

    useEffect(() => {
        if (teachr?.id) store.dispatch(getActiveClients(dataObject));
    }, [dataObject, teachr?.id, nameSearch, currentPage, limit]);

    // ── Contexte ──────────────────────────────────────────────────────────
    const contextValue: DeclarationContexteType = useMemo(() => ({
        teachr,
        dispatch,
        generalStep,
        step,
        handleChangeStep,
        clients: activeClientsState?.data ?? [],
        totalItems: activeClientsState?.totalItems ?? 0,
        currentPage,
        nameSearch,
        setNameSearch,
        paginate,
        pageLimit,
    }), [teachr, dispatch, generalStep, step, activeClientsState, currentPage, nameSearch, paginate, pageLimit]);

    const renderStepScreen = (step: number) => {
        switch (step) {
            case 1: return <FirstStep />;
            case 2: return <SecondStep />;
            case 3: return <ThirdStep />;
            case 4: return <LastStep />;
            case 5: return <FinishStep />;
            default: return <FirstStep />;
        }
    };

    return (
        <DeclarationProvider value={contextValue}>
            <IndexApp
                index={1}
                teachr={teachr}
                component={
                    <Layout
                        header={
                            <div className="px-1">
                                <BrandContent
                                    tag="h1"
                                    children={`Commencez à déclarer vos ${getActivePrestationBrandConfig("s")}`}
                                    type="homeSubtitle"
                                    className={`${common.subtitle} mb-2 ${classes.primaryText}`}
                                />
                                <BrandContent
                                    tag="div"
                                    children={`Veuillez noter que seuls les ${getActivePrestationBrandConfig("s")} à domicile ouvrent droit à l'avance immédiate de crédit d'impôt. Si vous avez dispensé des ${getActivePrestationBrandConfig("s")} en ligne, ceux-ci ne sont pas éligibles à ce dispositif et ne pourront pas être pris en compte dans votre déclaration.`}
                                    type="description"
                                    className={`lg:w-3/4 ${common.body} ${classes.text}`}
                                />
                            </div>
                        }
                    >
                        <div className="flex flex-col-reverse lg:flex-row gap-4 lg:py-2 p-1">
                            <div className="border border-gray-200 shadow-sm rounded-md bg-white lg:w-3/4 lg:py-4 p-1">
                                {renderStepScreen(currentStep)}
                            </div>
                            <div className="lg:w-1/4 border border-gray-200 shadow-sm rounded-md bg-white p-4">
                                <Stepper
                                    nextStep={handleChangeStep}
                                    steps={[
                                        `Choix ${getActiveClientBrandConfig('', "de l'", "du ")} 🎓`,
                                        `Planning 🗓️`,
                                        "Tarif horaire 💰",
                                        "Récapitulatif 📋",
                                    ]}
                                    currentStep={currentStep - 1}
                                />
                            </div>
                        </div>
                    </Layout>
                }
            />
        </DeclarationProvider>
    );
};

export default DeclarationPage;
